import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ChevronLeft, DollarSign, Plus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * P2P Post Sell Ad page.
 * - India: Bank Account form (Acc/IFSC/Holder/Bank)
 * - Pakistan: dynamic list of payment methods (Easypaisa, JazzCash, Bank, HBL, etc.)
 */
const P2PPostAdPage = () => {
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const api = authAxios();

  const params = new URLSearchParams(location.search);
  const source = params.get("source") === "game" ? "game" : "outside";

  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  // Form fields
  const [totalUsdt, setTotalUsdt] = useState("");
  const [price, setPrice] = useState("");
  const [minFiat, setMinFiat] = useState("");
  const [maxFiat, setMaxFiat] = useState("");
  const [notes, setNotes] = useState("");

  // India bank fields
  const [accNo, setAccNo] = useState("");
  const [ifsc, setIfsc] = useState("");
  const [holder, setHolder] = useState("");
  const [bankName, setBankName] = useState("");

  // Pakistan methods array
  const PK_METHODS = ["easypaisa", "jazzcash", "bank_transfer", "hbl", "meezan", "ubl"];
  const NG_METHODS = ["bank_transfer", "opay", "palmpay", "kuda", "moniepoint"];
  const [pkMethods, setPkMethods] = useState([{ method: "easypaisa", account_number: "", holder_name: "", extra_info: "" }]);
  const [ngMethods, setNgMethods] = useState([{ method: "bank_transfer", account_number: "", holder_name: "", extra_info: "" }]);

  useEffect(() => {
    api.get("/p2p/me/status")
      .then((r) => setStatus(r.data))
      .catch(() => {});
    // eslint-disable-next-line
  }, []);

  const isIndia = status?.country === "india";
  const isNigeria = status?.country === "nigeria";
  const fiatLabel = isIndia ? "INR" : isNigeria ? "NGN" : "PKR";
  const availableBalance = source === "outside" ? status?.wallets?.outside_usdt_balance : status?.wallets?.game_usdt_balance;

  const addPkMethod = () => setPkMethods([...pkMethods, { method: "bank_transfer", account_number: "", holder_name: "", extra_info: "" }]);
  const removePkMethod = (idx) => setPkMethods(pkMethods.filter((_, i) => i !== idx));
  const updatePkMethod = (idx, field, value) => {
    const next = [...pkMethods];
    next[idx][field] = value;
    setPkMethods(next);
  };

  const addNgMethod = () => setNgMethods([...ngMethods, { method: "bank_transfer", account_number: "", holder_name: "", extra_info: "" }]);
  const removeNgMethod = (idx) => setNgMethods(ngMethods.filter((_, i) => i !== idx));
  const updateNgMethod = (idx, field, value) => {
    const next = [...ngMethods];
    next[idx][field] = value;
    setNgMethods(next);
  };

  const handleSubmit = async () => {
    if (!totalUsdt || !price || !minFiat || !maxFiat) {
      toast.error("Please fill all required fields");
      return;
    }
    const tu = parseFloat(totalUsdt), pp = parseFloat(price);
    const mn = parseFloat(minFiat), mx = parseFloat(maxFiat);
    if (tu <= 0 || pp <= 0 || mn <= 0 || mx <= 0) {
      toast.error("All amounts must be positive");
      return;
    }
    if (mn > mx) { toast.error("Min cannot exceed Max"); return; }
    if (mx > tu * pp) { toast.error("Max limit exceeds total ad value"); return; }

    if (availableBalance != null && tu > availableBalance) {
      toast.error(`Insufficient balance. Available: ${availableBalance.toFixed(2)} USDT in ${source === "outside" ? "Stake" : "Game"} wallet`);
      return;
    }

    const payload = {
      side: "sell",
      source,
      total_amount_usdt: tu,
      price_per_usdt: pp,
      min_limit_fiat: mn,
      max_limit_fiat: mx,
      notes,
    };
    if (isIndia) {
      if (!accNo || !ifsc || !holder || !bankName) {
        toast.error("Please fill all bank details");
        return;
      }
      payload.bank_details = { account_number: accNo, ifsc, holder_name: holder, bank_name: bankName };
    } else if (isNigeria) {
      const valid = ngMethods.filter((p) => p.method && p.account_number && p.holder_name);
      if (valid.length === 0) {
        toast.error("Add at least one valid payment method");
        return;
      }
      payload.payment_methods = valid;
    } else {
      const valid = pkMethods.filter((p) => p.method && p.account_number && p.holder_name);
      if (valid.length === 0) {
        toast.error("Add at least one valid payment method");
        return;
      }
      payload.payment_methods = valid;
    }

    setBusy(true);
    try {
      const r = await api.post("/p2p/ads", payload);
      toast.success("Ad posted successfully!");
      navigate("/p2p/my-ads");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to post ad");
    }
    setBusy(false);
  };

  if (!status) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border flex items-center px-4 py-3">
        <button onClick={() => navigate(-1)} className="p-1 mr-2" data-testid="p2p-postad-back-btn">
          <ChevronLeft size={24} />
        </button>
        <h1 className="text-lg font-bold flex items-center gap-2">
          <DollarSign size={20} className="text-primary" /> Sell USDT — Post Ad
        </h1>
      </div>

      <div className="px-4 py-4 space-y-4">
        <div className="bg-card border border-border rounded-xl p-3 text-xs space-y-1">
          <div>Region: <span className="font-semibold uppercase text-primary">{status.country}</span></div>
          <div>Source Wallet: <span className="font-semibold text-primary">{source === "outside" ? "Main USDT (live)" : "Game USDT"}</span></div>
          <div>Available to Sell: <span className="font-semibold">{(availableBalance || 0).toFixed(4)} USDT</span></div>
        </div>

        {/* Core fields */}
        <div className="space-y-3">
          <Input label={`Total USDT to sell`} value={totalUsdt} onChange={setTotalUsdt} type="number" testId="ad-total-usdt" />
          <Input label={`Price per USDT (${fiatLabel})`} value={price} onChange={setPrice} type="number" testId="ad-price" />
          <div className="grid grid-cols-2 gap-3">
            <Input label={`Min ${fiatLabel}`} value={minFiat} onChange={setMinFiat} type="number" testId="ad-min" />
            <Input label={`Max ${fiatLabel}`} value={maxFiat} onChange={setMaxFiat} type="number" testId="ad-max" />
          </div>
          <Input label="Notes (optional, public)" value={notes} onChange={setNotes} testId="ad-notes" />
        </div>

        {/* Payment details */}
        {isIndia ? (
          <div className="space-y-3 pt-2">
            <div className="text-sm font-semibold border-l-4 border-primary pl-2">Bank Account Details (only Bank Transfer allowed for India)</div>
            <Input label="Account Number" value={accNo} onChange={setAccNo} testId="bank-acc" />
            <Input label="IFSC Code" value={ifsc} onChange={(v) => setIfsc(v.toUpperCase())} testId="bank-ifsc" />
            <Input label="Holder Name" value={holder} onChange={setHolder} testId="bank-holder" />
            <Input label="Bank Name" value={bankName} onChange={setBankName} testId="bank-name" />
          </div>
        ) : isNigeria ? (
          <div className="space-y-3 pt-2">
            <div className="text-sm font-semibold border-l-4 border-primary pl-2">Payment Methods (Nigeria)</div>
            <div className="text-[11px] text-yellow-300/80">Account numbers stay hidden in public listings — only buyers with approved KYC will see them.</div>
            {ngMethods.map((pm, idx) => (
              <div key={idx} className="bg-card border border-border rounded-xl p-3 space-y-2 relative">
                {ngMethods.length > 1 && (
                  <button onClick={() => removeNgMethod(idx)} className="absolute top-2 right-2 text-red-400" data-testid={`ng-remove-${idx}`}><X size={16} /></button>
                )}
                <select value={pm.method} onChange={(e) => updateNgMethod(idx, "method", e.target.value)}
                  className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm" data-testid={`ng-method-${idx}`}>
                  {NG_METHODS.map((m) => <option key={m} value={m}>{m.replace("_", " ").toUpperCase()}</option>)}
                </select>
                <Input label={pm.method === "bank_transfer" ? "Account Number (10 digits)" : "Phone Number / Account"} value={pm.account_number} onChange={(v) => updateNgMethod(idx, "account_number", v)} compact />
                <Input label="Account Holder Name" value={pm.holder_name} onChange={(v) => updateNgMethod(idx, "holder_name", v)} compact />
                <Input label={pm.method === "bank_transfer" ? "Bank Name (GTB / Access / UBA / etc)" : "Extra info (optional)"} value={pm.extra_info} onChange={(v) => updateNgMethod(idx, "extra_info", v)} compact />
              </div>
            ))}
            <button onClick={addNgMethod} className="w-full py-2 border border-dashed border-border rounded-lg text-xs flex items-center justify-center gap-1 text-primary" data-testid="ng-add-method">
              <Plus size={14} /> Add another method
            </button>
          </div>
        ) : (
          <div className="space-y-3 pt-2">
            <div className="text-sm font-semibold border-l-4 border-primary pl-2">Payment Methods</div>
            {pkMethods.map((pm, idx) => (
              <div key={idx} className="bg-card border border-border rounded-xl p-3 space-y-2 relative">
                {pkMethods.length > 1 && (
                  <button onClick={() => removePkMethod(idx)} className="absolute top-2 right-2 text-red-400"><X size={16} /></button>
                )}
                <select value={pm.method} onChange={(e) => updatePkMethod(idx, "method", e.target.value)}
                  className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm" data-testid={`pk-method-${idx}`}>
                  {PK_METHODS.map((m) => <option key={m} value={m}>{m.replace("_", " ").toUpperCase()}</option>)}
                </select>
                <Input label="Account Number" value={pm.account_number} onChange={(v) => updatePkMethod(idx, "account_number", v)} compact />
                <Input label="Holder Name" value={pm.holder_name} onChange={(v) => updatePkMethod(idx, "holder_name", v)} compact />
                <Input label="Extra Info (optional)" value={pm.extra_info} onChange={(v) => updatePkMethod(idx, "extra_info", v)} compact />
              </div>
            ))}
            <button onClick={addPkMethod} className="w-full py-2 border border-dashed border-border rounded-lg text-xs flex items-center justify-center gap-1 text-primary">
              <Plus size={14} /> Add another method
            </button>
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={busy}
          className="w-full py-3 bg-primary text-primary-foreground rounded-xl font-bold disabled:opacity-50"
          data-testid="post-ad-submit-btn"
        >
          {busy ? <Loader2 className="animate-spin inline" /> : "Post Sell Ad (Lock USDT)"}
        </button>
      </div>
    </div>
  );
};

const Input = ({ label, value, onChange, type = "text", testId, compact = false }) => (
  <div className={compact ? "" : "space-y-1"}>
    <label className={`block ${compact ? "text-[10px]" : "text-xs"} text-muted-foreground`}>{label}</label>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      type={type}
      className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm"
      data-testid={testId}
    />
  </div>
);

export default P2PPostAdPage;
